import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Receipt, ArrowLeft, Banknote, Stethoscope, Pill, TestTube, Scan, HeartHandshake, RefreshCw,
} from 'lucide-react';
import { toast } from 'react-toastify';
import createAuthedAxios from '../../hooks/useAxiosAuth';
import baseURL from '../../utils/baseURL';
import CollectPaymentModal from '../../components/billing/CollectPaymentModal';
import useHospitalCurrency from '../../hooks/useHospitalCurrency';
import { formatPrice } from '../../utils/currency';
import { hospitalPatientCode } from '../../utils/patientCode';

// ONE PLACE PER PATIENT (Phase 4, #16): every bill this patient has at this
// hospital — consult, pharmacy, lab, scan, care — with the outstanding total on
// top and payment collection inline.

const SOURCE_META = {
  consult: { labelKey: 'billing.consult', fallback: 'Consult', Icon: Stethoscope, tint: 'bg-teal-50 text-teal-700' },
  pharmacy: { labelKey: 'billing.pharmacy', fallback: 'Pharmacy', Icon: Pill, tint: 'bg-emerald-50 text-emerald-700' },
  lab: { labelKey: 'billing.lab', fallback: 'Lab', Icon: TestTube, tint: 'bg-violet-50 text-violet-700' },
  scan: { labelKey: 'billing.scans', fallback: 'Scan', Icon: Scan, tint: 'bg-sky-50 text-sky-700' },
  care: { labelKey: 'billing.care', fallback: 'Care', Icon: HeartHandshake, tint: 'bg-rose-50 text-rose-700' },
};

// Every figure is in the hospital's currency, read through utils/currency.js
// (P24): an invoice row names no currency of its own, and the page used to
// print the rupee sign for every hospital. Unknown -> the bare amount.
const money = (n, currency) => formatPrice(Number(n) || 0, currency, { decimals: 2 });

const STATUS_TINT = {
  paid: 'bg-emerald-50 text-emerald-700',
  partial: 'bg-amber-50 text-amber-700',
  unpaid: 'bg-rose-50 text-rose-600',
  cancelled: 'bg-gray-100 text-gray-500',
};

const PatientBilling = () => {
  const { t } = useTranslation();
  const { patientId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payInvoice, setPayInvoice] = useState(null);
  const currency = useHospitalCurrency();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const axiosInstance = createAuthedAxios();
      const { data: d } = await axiosInstance.get(`${baseURL}/billing/patient/${patientId}`);
      setData(d);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('billing.load_failed', 'Could not load the bills'));
    } finally {
      setLoading(false);
    }
  }, [patientId, t]);

  useEffect(() => { load(); }, [load]);

  const summary = data?.summary || {};
  const invoices = data?.invoices || [];

  return (
    <div data-testid="patient-billing-page">
      <div className="flex items-center gap-3 mb-5">
        <button onClick={() => navigate(-1)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors cursor-pointer">
          <ArrowLeft size={18} />
        </button>
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#4ABDBD] to-[#2D8A8A] flex items-center justify-center shadow-md">
          <Receipt size={20} className="text-white" />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold text-gray-800">{t('billing.patient_title', 'Patient billing')}</h1>
          {/* The hospital's own number (APO034) when the tenant has one — never
              the patientId (owner, 29 Sep 2026: no system id on screen). */}
          <p className="text-xs text-gray-400">
            {[data?.patient?.name || t('common.patient_label', 'Patient'), hospitalPatientCode(data?.patient)].filter(Boolean).join(' · ')}
          </p>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500 transition-colors cursor-pointer" title={t('common.refresh', 'Refresh')}>
          <RefreshCw size={17} />
        </button>
      </div>

      {loading ? (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[...Array(4)].map((_, i) => <div key={i} className="animate-pulse h-20 bg-white rounded-2xl shadow-sm" />)}
          </div>
          <div className="animate-pulse h-64 bg-white rounded-2xl shadow-sm" />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            <div className="bg-white rounded-2xl shadow-md p-4">
              <p className="text-[11px] text-gray-400 mb-1">{t('billing.outstanding', 'Outstanding')}</p>
              <p className={`text-lg font-bold ${Number(data?.outstanding) > 0 ? 'text-rose-600' : 'text-gray-800'}`}>{money(data?.outstanding, currency)}</p>
            </div>
            {Object.entries(summary).map(([src, s]) => {
              const meta = SOURCE_META[src] || SOURCE_META.pharmacy;
              return (
                <div key={src} className="bg-white rounded-2xl shadow-md p-4">
                  <div className="flex items-center gap-1.5 mb-1">
                    <meta.Icon size={13} className="text-gray-400" />
                    <p className="text-[11px] text-gray-400">{t(meta.labelKey, meta.fallback)}</p>
                  </div>
                  <p className="text-lg font-bold text-gray-800">{money(s.billed, currency)}</p>
                  <p className="text-[11px] text-gray-400">{s.count} {t('billing.bills', 'bills')}</p>
                </div>
              );
            })}
          </div>

          <div className="bg-white rounded-2xl shadow-md overflow-hidden">
            {invoices.length === 0 ? (
              <div className="p-12 text-center">
                <Receipt size={40} className="mx-auto text-gray-200 mb-3" />
                <p className="text-sm text-gray-500">{t('billing.no_bills', 'No bills for this patient yet.')}</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 border-b border-gray-100">
                      <th className="px-5 py-3 font-medium">{t('billing.bill', 'Bill')}</th>
                      <th className="px-5 py-3 font-medium">{t('billing.source', 'Service')}</th>
                      <th className="px-5 py-3 font-medium text-right">{t('billing.amount', 'Amount')}</th>
                      <th className="px-5 py-3 font-medium text-right">{t('billing.balance', 'Balance')}</th>
                      <th className="px-5 py-3 font-medium text-center">{t('billing.status', 'Status')}</th>
                      <th className="px-5 py-3 font-medium text-right">{t('billing.action', 'Action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((inv) => {
                      const meta = SOURCE_META[inv.billSource] || SOURCE_META.pharmacy;
                      return (
                        <tr key={inv.invoiceId} className="border-b border-gray-50 hover:bg-gray-50/60 transition-colors">
                          <td className="px-5 py-3.5">
                            <p className="font-medium text-gray-800">{inv.invoiceNo}</p>
                            <p className="text-[11px] text-gray-400">
                              {inv.invoiceDate ? new Date(inv.invoiceDate).toLocaleDateString() : ''}
                              {inv.invoiceType === 'credit_note' ? ` · ${t('billing.credit_note', 'Credit note')}` : ''}
                            </p>
                          </td>
                          <td className="px-5 py-3.5">
                            <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${meta.tint}`}>
                              <meta.Icon size={12} /> {t(meta.labelKey, meta.fallback)}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-right font-semibold text-gray-800">{money(inv.grandTotal, currency)}</td>
                          <td className="px-5 py-3.5 text-right text-gray-600">{money(inv.balance, currency)}</td>
                          <td className="px-5 py-3.5 text-center">
                            <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${STATUS_TINT[inv.status] || 'bg-gray-100 text-gray-500'}`}>
                              {inv.status}
                            </span>
                          </td>
                          <td className="px-5 py-3.5 text-right">
                            {inv.invoiceType === 'sale' && inv.status !== 'cancelled' && Number(inv.balance) > 0 && (
                              <button
                                onClick={() => setPayInvoice(inv)}
                                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-lg bg-amber-50 text-amber-700 text-xs font-semibold hover:bg-amber-100 transition-colors cursor-pointer"
                                data-testid={`patient-billing-collect-${inv.invoiceId}`}
                              >
                                <Banknote size={13} /> {t('billing.collect_short', 'Collect')}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}

      {payInvoice && (
        <CollectPaymentModal
          invoice={payInvoice}
          currency={currency}
          onClose={() => setPayInvoice(null)}
          onPaid={() => { setPayInvoice(null); load(); }}
        />
      )}
    </div>
  );
};

export default PatientBilling;
